/**
 * ThemeProvider — owns the current theme choice for the running app.
 *
 * Applies the choice to <html>, persists it locally, and (for "Match system")
 * re-applies the instant the OS flips between light and dark. The boot script
 * in index.html has already applied the right theme before first paint; this
 * provider takes over so React can drive changes from Settings.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { darkPalette, lightPalette, type Colors } from '@turn-connect/ui';
import {
  type ThemeChoice,
  type ResolvedTheme,
  DEFAULT_CHOICE,
  isThemeChoice,
  readStoredChoice,
  writeStoredChoice,
  resolveChoice,
  applyResolvedTheme,
} from './themeController';

interface ThemeContextValue {
  /** What the user picked. */
  choice: ThemeChoice;
  /** What is actually applied right now (system resolved to light or dark). */
  resolved: ResolvedTheme;
  /** Change the theme. Applies immediately and persists locally; callers that
   *  also want it saved server-side pass persistRemote. */
  setChoice: (choice: ThemeChoice, persistRemote?: (choice: ThemeChoice) => void) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  choice: DEFAULT_CHOICE,
  resolved: resolveChoice(DEFAULT_CHOICE),
  setChoice: () => undefined,
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => readStoredChoice());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveChoice(readStoredChoice()));

  // Apply + persist whenever the choice changes.
  useEffect(() => {
    const r = resolveChoice(choice);
    setResolved(r);
    applyResolvedTheme(r);
    writeStoredChoice(choice);
  }, [choice]);

  // While on "system", follow the OS the moment it changes.
  useEffect(() => {
    if (choice !== 'system' || typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const r = resolveChoice('system');
      setResolved(r);
      applyResolvedTheme(r);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [choice]);

  const setChoice = useCallback<ThemeContextValue['setChoice']>((next, persistRemote) => {
    setChoiceState(next);
    persistRemote?.(next);
  }, []);

  const value = useMemo(() => ({ choice, resolved, setChoice }), [choice, resolved, setChoice]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/** Read and change the theme from anywhere in the tree. */
export const useThemeChoice = (): ThemeContextValue => useContext(ThemeContext);

/**
 * The RESOLVED literal palette for the active theme.
 *
 * Use this instead of `theme.colors.*` in contexts where a CSS variable does
 * not resolve — SVG presentation attributes (`fill`/`stroke`), canvas, or any
 * value handed to a library that renders outside CSS. It returns concrete hex
 * and re-renders when the theme flips (unlike `theme.colors`, which is a var()
 * string that a `<line stroke={…}>` attribute would render black).
 */
export const useThemeColors = (): Colors => {
  const { resolved } = useThemeChoice();
  return resolved === 'light' ? lightPalette : darkPalette;
};

/**
 * At login, apply the theme the server has for this user (the source of truth),
 * which also caches it locally for the next boot. Runs once per mount, once the
 * server value is known; a local change made this session already matches.
 */
export const useThemeSync = (serverTheme: string | undefined): void => {
  const { choice, setChoice } = useThemeChoice();
  const synced = useRef(false);
  useEffect(() => {
    if (synced.current || !serverTheme) return;
    if (isThemeChoice(serverTheme) && serverTheme !== choice) setChoice(serverTheme);
    synced.current = true;
  }, [serverTheme, choice, setChoice]);
};
